import React from 'react';
import { CheckCircle, Star, Smartphone, Users, Lock, TrendingUp, CreditCard, ShoppingCart, AlertCircle, FileText, PhoneCall, Gift, Zap, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useState } from 'react';

const END_DATE = 'July 31, 2024'; // Update as needed

const features = [
  {
    icon: <ShoppingCart className="w-7 h-7 text-jordy-blue-600" />,
    title: 'Track Inventory in real time',
    desc: 'Get low-stock alerts and never run out of bestsellers.'
  },
  {
    icon: <FileText className="w-7 h-7 text-jordy-blue-600" />,
    title: 'Automate Billing',
    desc: 'GST/VAT-ready invoicing and seamless record-keeping.'
  },
  {
    icon: <CreditCard className="w-7 h-7 text-jordy-blue-600" />,
    title: 'Integrate Payments',
    desc: 'Stripe, PayPal, PhonePe, Razorpay, and more.'
  },
  {
    icon: <Smartphone className="w-7 h-7 text-jordy-blue-600" />,
    title: 'Mobile Ordering',
    desc: 'Your branded customer app for easy ordering.'
  },
  {
    icon: <Gift className="w-7 h-7 text-jordy-blue-600" />,
    title: 'Loyalty Programs',
    desc: 'Collect customer data and boost repeat business.'
  },
  {
    icon: <TrendingUp className="w-7 h-7 text-jordy-blue-600" />,
    title: 'Business Analytics',
    desc: 'See sales, trends, and insights for smarter decisions.'
  },
  {
    icon: <Users className="w-7 h-7 text-jordy-blue-600" />,
    title: 'Multi-User Access',
    desc: 'Roles for managers & staff, secure and easy.'
  },
  {
    icon: <Lock className="w-7 h-7 text-jordy-blue-600" />,
    title: 'Stay Compliant',
    desc: 'Automated reports and contracts for peace of mind.'
  }
];

const whyChoose = [
  '100% Custom Solution – No unnecessary features',
  'Designed for Smoke Shop workflows',
  'Affordable pricing & one-time setup',
  'Integration with your existing tools (POS, Zoho Books, WhatsApp API)',
  'Ongoing support & updates'
];

const testimonials = [
  {
    quote: 'This ERP transformed our operations — from inventory to customer handling!',
    name: 'Mike R.',
    location: 'Houston'
  },
  {
    quote: 'We finally have full control over our smoke shop. The custom app is a game-changer.',
    name: 'Lisa T.',
    location: 'Miami'
  }
];

const faqs = [
  {
    q: 'Can I migrate from my current POS?',
    a: 'Yes. We offer full onboarding and data migration.'
  },
  {
    q: 'Can the app be white-labeled?',
    a: 'Absolutely. Your branding, your app.'
  }
];

const ErpSmokeShopsPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [form, setForm] = useState<{
    name: string;
    contact: string;
    whatsapp: string;
    software: string;
    budget: string;
    info: string;
    [key: string]: string;
  }>({
    name: '',
    contact: '',
    whatsapp: '',
    software: '',
    budget: '',
    info: ''
  });
  const [formError, setFormError] = useState('');
  const [formTouched, setFormTouched] = useState<{[k:string]:boolean}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const budgetOptions = [
    '',
    'Under $1,000',
    '$1,000 - $5,000',
    '$5,000 - $10,000',
    '$10,000 - $25,000',
    'Over $25,000'
  ];

  const validate = (field: string) => {
    if (!formTouched[field]) return '';
    if (['name','contact','whatsapp','budget'].includes(field) && !form[field]) return 'Required';
    if (field === 'contact' && form.contact && !/^\+?\d{7,15}$/.test(form.contact)) return 'Enter a valid number';
    if (field === 'whatsapp' && form.whatsapp && !/^\+?\d{7,15}$/.test(form.whatsapp)) return 'Enter a valid WhatsApp number';
    return '';
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormTouched({ ...formTouched, [e.target.name]: true });
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setFormSubmitted(false);
    setForm({ name: '', contact: '', whatsapp: '', software: '', budget: '', info: '' });
    setFormError('');
    setFormTouched({}); // Reset touched fields on open
  };
  const handleCloseModal = () => setIsModalOpen(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormTouched({ name: true, contact: true, whatsapp: true, budget: true });
    if (!form.name || !form.contact || !form.whatsapp || !form.budget) {
      setFormError('Please fill all required fields.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/erp-smokeshops', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });

      if (response.ok) {
        setFormSubmitted(true);
        setFormError('');
        setTimeout(() => handleCloseModal(), 2000);
      } else {
        throw new Error('Failed to submit request');
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      setFormError('There was an error submitting your request. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>ERP for Smoke Shops | VB Entreprise | Premium IT & Digital Marketing Agency for US, UK & Canada Businesses</title>
      </Helmet>
      <div className="bg-white min-h-screen">
        {/* Hero Section */}
        <AnimatedSection>
          <section className="relative py-24 flex items-center justify-center bg-gradient-to-br from-jordy-blue-50 via-white to-jordy-blue-100 overflow-hidden">
            {/* Animated floating circles */}
            <motion.div
              className="absolute top-10 left-10 w-32 h-32 bg-jordy-blue-200 rounded-full opacity-20"
              animate={{ y: [0, -20, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="absolute bottom-10 right-10 w-24 h-24 bg-jordy-blue-300 rounded-full opacity-20"
              animate={{ y: [0, 20, 0], scale: [1, 0.9, 1] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            />
            <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
              <motion.h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                <span className="bg-gradient-to-r from-jordy-blue-600 to-jordy-blue-800 bg-clip-text text-transparent">Run Your Smoke Shop Smarter</span>
                <br />with Custom ERP & Mobile App Solutions
              </motion.h1>
              <motion.h2 className="text-2xl md:text-3xl text-jordy-blue-700 font-semibold mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
                Simplify operations, boost sales, manage inventory, and engage customers — all from one powerful system designed exclusively for smoke shops.
              </motion.h2>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}>
                <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-2 flex items-center justify-center">Problem Statement</h3>
                <p className="text-gray-600 text-base md:text-lg mb-6">Tired of juggling spreadsheets, POS issues, and poor customer tracking?<br/>Manual systems are costing you time, money, and missed opportunities.</p>
              </motion.div>
            </div>
          </section>
        </AnimatedSection>
        {/* Features Section */}
        <AnimatedSection>
          <section className="py-16">
            <div className="max-w-6xl mx-auto px-4">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-10">What You Get</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
                {features.map((f, i) => (
                  <motion.div key={i} className="bg-white rounded-xl shadow-md p-6 flex flex-col items-center text-center" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}>
                    {f.icon}
                    <h4 className="font-semibold text-lg text-gray-900 mt-4 mb-2">{f.title}</h4>
                    <p className="text-gray-600 text-sm">{f.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>
        {/* POS System Highlight Section */}
        <AnimatedSection>
          <section className="py-12 bg-gradient-to-r from-jordy-blue-100 to-jordy-blue-200 text-center">
            <div className="max-w-3xl mx-auto px-4">
              <h2 className="text-2xl md:text-3xl font-bold text-jordy-blue-800 mb-4">Integrated POS System</h2>
              <p className="text-lg md:text-xl text-jordy-blue-900 mb-4">You will also get a POS system, allowing you to manage your online and offline business together seamlessly. Track sales, inventory, and customer data in one place—whether in-store or online.</p>
            </div>
          </section>
        </AnimatedSection>

        {/* Why Choose Us */}
        <AnimatedSection>
          <section className="py-16 bg-jordy-blue-50">
            <div className="max-w-4xl mx-auto px-4 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center justify-center"><Star className="w-6 h-6 text-jordy-blue-600 mr-2" />Why Choose Us?</h2>
              <ul className="space-y-3 mb-8">
                {whyChoose.map((item, i) => (
                  <li key={i} className="flex items-center justify-center text-gray-700 text-base md:text-lg">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </AnimatedSection>

        {/* Limited Time Offer */}
        <AnimatedSection>
          <section className="py-12 bg-gradient-to-r from-jordy-blue-500 to-jordy-blue-600 text-white text-center">
            <div className="max-w-2xl mx-auto px-4">
              <div className="flex items-center justify-center mb-4">
                <Zap className="w-7 h-7 text-yellow-300 mr-2" />
                <span className="text-lg font-bold">🔥 Limited Time Offer</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-2">15% OFF for Smoke Shops in the USA!</h3>
              <p className="text-lg mb-2">Use Code: <span className="bg-white text-jordy-blue-600 px-2 py-1 rounded font-mono">SMOKE15</span> – Valid until {END_DATE}</p>
            </div>
          </section>
        </AnimatedSection>

        {/* CTA Section */}
        <AnimatedSection>
          <section className="py-16">
            <div className="max-w-3xl mx-auto px-4 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Ready to Future-Proof Your Business?</h2>
              <p className="text-lg text-gray-700 mb-8">👉 Schedule a free demo or call now.</p>
              <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
                <button onClick={handleOpenModal} className="bg-jordy-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-jordy-blue-700 transition-colors flex items-center justify-center text-lg">
                  Book Demo <ArrowRight className="w-5 h-5 ml-2" />
                </button>
                <button onClick={handleOpenModal} className="bg-yellow-400 text-jordy-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-yellow-300 transition-colors flex items-center justify-center text-lg">
                  Get Offer <ArrowRight className="w-5 h-5 ml-2" />
                </button>
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Testimonials */}
        <AnimatedSection>
          <section className="py-16 bg-gray-50">
            <div className="max-w-4xl mx-auto px-4">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-10">What Our Clients Say</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {testimonials.map((t, i) => (
                  <motion.div key={i} className="bg-white rounded-xl shadow-md p-6 flex flex-col" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                    <p className="text-gray-700 text-lg mb-4">“{t.quote}”</p>
                    <div className="flex items-center space-x-2 mt-auto">
                      <Users className="w-5 h-5 text-jordy-blue-600" />
                      <span className="font-semibold text-gray-900">{t.name}</span>
                      <span className="text-gray-500">{t.location}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* FAQ Section */}
        <AnimatedSection>
          <section className="py-16">
            <div className="max-w-3xl mx-auto px-4">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8">FAQ</h2>
              <div className="space-y-6">
                {faqs.map((faq, i) => (
                  <motion.div key={i} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.q}</h3>
                    <p className="text-gray-600">{faq.a}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>
      </div>
      {/* Modal Popup Form */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 relative animate-fadeInUp">
            <button onClick={handleCloseModal} aria-label="Close form" className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold">&times;</button>
            {!formSubmitted ? (
              <form onSubmit={handleSubmit} className="space-y-5" aria-label="Request Demo or Offer">
                <h3 className="text-2xl font-bold text-jordy-blue-700 mb-2 text-center">Request a Demo / Offer</h3>
                {formError && <div className="text-red-500 text-sm text-center">{formError}</div>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input id="name" name="name" value={form.name} onChange={handleChange} onBlur={handleBlur} required placeholder="Your full name" className={`w-full px-4 py-2 border ${validate('name') ? 'border-red-400' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-jordy-blue-500`} aria-required="true" aria-invalid={!!validate('name')} />
                    {validate('name') && <span className="text-xs text-red-500">{validate('name')}</span>}
                  </div>
                  <div>
                    <label htmlFor="contact" className="block text-sm font-medium text-gray-700 mb-1">Contact Number *</label>
                    <input id="contact" name="contact" type="tel" value={form.contact} onChange={handleChange} onBlur={handleBlur} required placeholder="e.g. +1234567890" className={`w-full px-4 py-2 border ${validate('contact') ? 'border-red-400' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-jordy-blue-500`} aria-required="true" aria-invalid={!!validate('contact')} />
                    {validate('contact') && <span className="text-xs text-red-500">{validate('contact')}</span>}
                  </div>
                  <div>
                    <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 mb-1">Whatsapp *</label>
                    <input id="whatsapp" name="whatsapp" type="tel" value={form.whatsapp} onChange={handleChange} onBlur={handleBlur} required placeholder="e.g. +1234567890" className={`w-full px-4 py-2 border ${validate('whatsapp') ? 'border-red-400' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-jordy-blue-500`} aria-required="true" aria-invalid={!!validate('whatsapp')} />
                    {validate('whatsapp') && <span className="text-xs text-red-500">{validate('whatsapp')}</span>}
                  </div>
                  <div>
                    <label htmlFor="software" className="block text-sm font-medium text-gray-700 mb-1">Current software you use</label>
                    <input id="software" name="software" value={form.software} onChange={handleChange} onBlur={handleBlur} placeholder="POS, Zoho, etc." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-jordy-blue-500" />
                  </div>
                  <div>
                    <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1">Your budget *</label>
                    <select id="budget" name="budget" value={form.budget} onChange={handleChange} onBlur={handleBlur} required className={`w-full px-4 py-2 border ${validate('budget') ? 'border-red-400' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-jordy-blue-500`} aria-required="true" aria-invalid={!!validate('budget')}>
                      {budgetOptions.map(opt => <option key={opt} value={opt}>{opt ? opt : 'Select budget range'}</option>)}
                    </select>
                    {validate('budget') && <span className="text-xs text-red-500">{validate('budget')}</span>}
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="info" className="block text-sm font-medium text-gray-700 mb-1">Any information you need to disclose</label>
                    <textarea id="info" name="info" value={form.info} onChange={handleChange} onBlur={handleBlur} placeholder="Share anything important..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-jordy-blue-500" rows={3} />
                  </div>
                </div>
                <button type="submit" disabled={isSubmitting} className="w-full bg-jordy-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-jordy-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center">
                  {isSubmitting ? <span className="animate-spin mr-2">⏳</span> : null} Submit
                </button>
              </form>
            ) : (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
                <p className="text-gray-600">We will contact you shortly.</p>
                <button onClick={handleCloseModal} className="mt-6 bg-jordy-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-jordy-blue-700 transition-colors">Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ErpSmokeShopsPage; 